/**
 * FPAFeedbackPage — Cash-to-FP&A Feedback Loop (Feature 5).
 * Displays AI-generated feedback items with accept/reject workflow,
 * impact summary, and status KPIs.
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useFPAFeedback, useUpdateFPAFeedback } from './api';
import { formatCurrency } from './utils';
import { cn } from '../../shared/lib/cn';
import type { FPAFeedback } from './types';

const TYPE_COLORS: Record<string, string> = {
  timing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  rate: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  volume: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  mix: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  structural: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
};

function statusBadge(status: string) {
  switch (status) {
    case 'accepted':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  }
}

function confidenceBadge(level?: string) {
  if (!level) return null;
  const cls = level === 'high'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : level === 'medium'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', cls)}>
      {level}
    </span>
  );
}

export default function FPAFeedbackPage() {
  const { data: summary } = useForecastSummary();
  const forecastId = summary?.forecast_id ?? '';
  const { data: feedback, isLoading } = useFPAFeedback(forecastId);
  const updateFeedback = useUpdateFPAFeedback();

  const counts = useMemo(() => {
    if (!feedback) return { pending: 0, accepted: 0, rejected: 0, totalImpact: 0 };
    return {
      pending: feedback.filter(f => f.status === 'pending').length,
      accepted: feedback.filter(f => f.status === 'accepted').length,
      rejected: feedback.filter(f => f.status === 'rejected').length,
      totalImpact: feedback.reduce((s, f) => s + (f.cash_impact_13w ?? 0), 0),
    };
  }, [feedback]);

  const impactSummary = useMemo(() => {
    if (!feedback) return { acceptedImpact: 0, rejectedImpact: 0, pendingImpact: 0 };
    return {
      acceptedImpact: feedback.filter(f => f.status === 'accepted').reduce((s, f) => s + (f.cash_impact_13w ?? 0), 0),
      rejectedImpact: feedback.filter(f => f.status === 'rejected').reduce((s, f) => s + (f.cash_impact_13w ?? 0), 0),
      pendingImpact: feedback.filter(f => f.status === 'pending').reduce((s, f) => s + (f.cash_impact_13w ?? 0), 0),
    };
  }, [feedback]);

  if (isLoading || !forecastId) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-52 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="grid grid-cols-4 gap-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-16 bg-gray-200 dark:bg-dark-surface-2 rounded" />
                ))}
              </div>
              <div className="h-40 bg-gray-200 dark:bg-dark-surface-2 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!feedback?.length) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
              FP&A Feedback Loop
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              No feedback items generated yet. Run the forecast engine to generate cash-to-budget feedback.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-2">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Pending</p>
            <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{counts.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Accepted</p>
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{counts.accepted}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Rejected</p>
            <p className="text-xl font-bold text-red-600 dark:text-red-400">{counts.rejected}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total 13W Cash Impact</p>
            <p className={cn('text-xl font-bold', counts.totalImpact >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              {formatCurrency(counts.totalImpact, true)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Impact Summary Bar */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-3">
            Impact Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 rounded bg-emerald-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Accepted Impact</p>
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(impactSummary.acceptedImpact, true)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 rounded bg-red-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Rejected Impact</p>
                <p className="text-lg font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(impactSummary.rejectedImpact, true)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 rounded bg-amber-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Pending Impact</p>
                <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                  {formatCurrency(impactSummary.pendingImpact, true)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {feedback.map((item: FPAFeedback) => (
          <Card key={item.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn(
                    'px-2 py-0.5 rounded text-xs font-medium',
                    TYPE_COLORS[item.feedback_type] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                  )}>
                    {item.feedback_type}
                  </span>
                  <span className={cn('px-2 py-0.5 rounded text-xs font-medium', statusBadge(item.status))}>
                    {item.status}
                  </span>
                  {confidenceBadge(item.confidence_level)}
                </div>
              </div>

              <h4 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-1">
                {item.metric_name}
              </h4>

              {(item.current_assumption != null || item.recommended_adjustment != null) && (
                <div className="flex items-center gap-2 mb-2 text-sm">
                  <span className="tabular-nums text-gray-600 dark:text-gray-400">
                    {item.current_assumption != null ? formatCurrency(item.current_assumption) : '—'}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="tabular-nums font-medium text-charcoal dark:text-gray-100">
                    {item.recommended_adjustment != null ? formatCurrency(item.recommended_adjustment) : '—'}
                  </span>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-3">
                {item.adjustment_rationale}
              </p>

              <div className="flex items-center justify-between">
                <div>
                  {item.cash_impact_13w != null && (
                    <span className={cn(
                      'text-sm font-bold',
                      item.cash_impact_13w >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                    )}>
                      {item.cash_impact_13w >= 0 ? '+' : ''}{formatCurrency(item.cash_impact_13w, true)} / 13w
                    </span>
                  )}
                </div>

                {item.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateFeedback.mutate({ id: item.id, status: 'accepted' })}
                      disabled={updateFeedback.isPending}
                      className="px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => updateFeedback.mutate({ id: item.id, status: 'rejected' })}
                      disabled={updateFeedback.isPending}
                      className="px-3 py-1 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
